import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Mic, Video, Target, Clock, Zap, MessageSquare, Shield, Play, Timer } from 'lucide-react';
import { useState, useEffect } from 'react';

const RoleplayShowcase = () => {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const [activeMode, setActiveMode] = useState(0);

  // Auto-rotate through modes
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setActiveMode(prev => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, [isVisible]);

  const modes = [
    {
      id: 'roleplay',
      title: 'Role-Play',
      subtitle: 'Full Sales Conversations',
      description: 'Practice complete discovery-to-close conversations with AI buyers who push back on weak questions and reward strong SPIN technique.',
      color: 'primary',
      icon: MessageSquare,
      features: [
        { icon: Mic, text: 'Voice or text input modes' },
        { icon: Video, text: 'AI sees your expressions & gestures' },
        { icon: Target, text: 'Live SPIN & discovery tracking' },
        { icon: Clock, text: 'Timed 15-minute sessions' },
      ],
      mockup: (
        <div className="space-y-3">
          <div className="flex justify-end">
            <div className="bg-primary text-primary-foreground px-4 py-2 rounded-2xl rounded-br-md text-sm max-w-[80%]">
              Can you tell me about your current packaging challenges?
            </div>
          </div>
          <div className="flex justify-start">
            <div className="glass px-4 py-2 rounded-2xl rounded-bl-md text-sm max-w-[80%]">
              We've had stacking issues. Damage runs 5-10% per affected batch.
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <div className="px-2 py-1 rounded glass text-xs">
              <span className="text-emerald-400">S</span>
              <span className="text-amber-400 ml-1">P</span>
              <span className="text-primary ml-1">I</span>
            </div>
            <div className="px-2 py-1 rounded glass text-xs text-muted-foreground">
              Discovery: <span className="text-primary">42%</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'speedsell',
      title: 'Speed-Sell',
      subtitle: '90-Second Elevator Pitch',
      description: 'Master the art of the quick pitch. Engage the buyer, deliver a compelling S.T.A.R.R. story, and close strong — all in 90 seconds.',
      color: 'accent',
      icon: Timer,
      features: [
        { icon: Video, text: 'Camera-enabled recording' },
        { icon: Play, text: 'Engage → Story → Close format' },
        { icon: Clock, text: '90-second countdown timer' },
        { icon: Target, text: 'S.T.A.R.R. story framework scoring' },
      ],
      mockup: (
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-accent/20 flex items-center justify-center">
            <span className="font-display text-3xl font-bold text-accent">87</span>
          </div>
          <div className="text-sm text-muted-foreground">seconds remaining</div>
          <div className="flex justify-center gap-2">
            <div className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 text-xs font-medium">
              Engage ✓
            </div>
            <div className="px-3 py-1.5 rounded-lg bg-accent/20 text-accent text-xs font-medium animate-pulse">
              S.T.A.R.R. Story
            </div>
            <div className="px-3 py-1.5 rounded-lg glass text-muted-foreground text-xs">
              Close
            </div>
          </div>
          <div className="w-full h-2 rounded-full bg-muted/30 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-accent to-emerald-400 rounded-full" style={{ width: '35%' }} />
          </div>
        </div>
      ),
    },
    {
      id: 'objections',
      title: 'Objection Drills',
      subtitle: 'Rapid-Fire Objection Handling',
      description: 'Master the Clarify-Address-Confirm framework with focused drills. Handle objections until you get explicit buyer acceptance.',
      color: 'amber',
      icon: Zap,
      features: [
        { icon: Zap, text: 'Rapid-fire objection practice' },
        { icon: Shield, text: '"Stay in the Ring" challenge mode' },
        { icon: Target, text: 'CAC framework scoring' },
        { icon: MessageSquare, text: 'Must confirm resolution to advance' },
      ],
      mockup: (
        <div className="space-y-3">
          <div className="flex justify-start">
            <div className="glass px-4 py-2 rounded-2xl rounded-bl-md text-sm max-w-[80%] border border-amber-500/30">
              <span className="text-amber-400 text-xs block mb-1">Objection #3</span>
              "Your pricing is 15% higher than our current supplier."
            </div>
          </div>
          <div className="flex justify-end">
            <div className="bg-amber-500/20 text-amber-100 px-4 py-2 rounded-2xl rounded-br-md text-sm max-w-[80%]">
              I hear that pricing is a concern. Can you help me understand what's driving that comparison?
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <div className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-xs">Clarify ✓</div>
            <div className="px-2 py-1 rounded bg-amber-500/20 text-amber-400 text-xs animate-pulse">Address</div>
            <div className="px-2 py-1 rounded glass text-muted-foreground text-xs">Confirm</div>
          </div>
        </div>
      ),
    },
  ];

  const colorClasses = {
    primary: {
      bg: 'bg-primary/10',
      border: 'border-primary/30',
      text: 'text-primary',
      glow: 'shadow-[0_0_40px_-10px_hsl(199_89%_48%/0.4)]',
    },
    accent: {
      bg: 'bg-accent/10',
      border: 'border-accent/30',
      text: 'text-accent',
      glow: 'shadow-[0_0_40px_-10px_hsl(185_85%_50%/0.4)]',
    },
    amber: {
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/30',
      text: 'text-amber-400',
      glow: 'shadow-[0_0_40px_-10px_hsl(45_93%_47%/0.4)]',
    },
  };

  return (
    <section ref={sectionRef} className="py-20 sm:py-32 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className={`text-center max-w-3xl mx-auto mb-12 sm:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-sm font-medium text-primary mb-4 border border-primary/20">
            <Zap className="w-4 h-4" />
            Three Ways to Practice
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Practice Like It's{' '}
            <span className="text-gradient bg-gradient-to-r from-primary via-accent to-amber-400 bg-clip-text text-transparent">
              Competition Day
            </span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Master every aspect of sales with three focused practice modes
          </p>
        </div>

        {/* Mode Selector Tabs */}
        <div className={`flex justify-center gap-2 sm:gap-4 mb-8 sm:mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {modes.map((mode, index) => {
            const colors = colorClasses[mode.color as keyof typeof colorClasses];
            const Icon = mode.icon;
            return (
              <button
                key={mode.id}
                onClick={() => setActiveMode(index)}
                className={`px-4 sm:px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                  activeMode === index
                    ? `${colors.bg} ${colors.text} border ${colors.border} ${colors.glow}`
                    : 'glass text-muted-foreground hover:text-foreground hover:bg-foreground/5'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{mode.title}</span>
              </button>
            );
          })}
        </div>

        {/* Mode Cards */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Description */}
          <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            {modes.map((mode, index) => {
              const colors = colorClasses[mode.color as keyof typeof colorClasses];
              const Icon = mode.icon;
              return (
                <div
                  key={mode.id}
                  className={`transition-all duration-500 ${activeMode === index ? 'opacity-100 block' : 'opacity-0 hidden'}`}
                >
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${colors.bg} ${colors.text} text-sm font-medium mb-4`}>
                    <Icon className="w-4 h-4" />
                    {mode.subtitle}
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold mb-4 text-foreground">
                    {mode.title}
                  </h3>
                  <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                    {mode.description}
                  </p>
                  <div className="space-y-3">
                    {mode.features.map((feature, i) => (
                      <div 
                        key={i}
                        className="flex items-center gap-3 p-3 rounded-xl glass hover:bg-foreground/5 transition-colors group"
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colors.bg} ${colors.text} group-hover:scale-110 transition-transform`}>
                          <feature.icon className="w-4 h-4" />
                        </div>
                        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right - Mockup */}
          <div className={`transition-all duration-500 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            {modes.map((mode, index) => {
              const colors = colorClasses[mode.color as keyof typeof colorClasses];
              return (
                <div
                  key={mode.id}
                  className={`transition-all duration-500 ${activeMode === index ? 'opacity-100 block' : 'opacity-0 hidden'}`}
                >
                  {/* Glow */}
                  <div className={`absolute inset-0 ${colors.bg} blur-3xl opacity-30 scale-110`} />
                  
                  {/* Card */}
                  <div className={`relative glass rounded-2xl sm:rounded-3xl overflow-hidden border ${colors.border} transition-all duration-300`}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-background/50 border-b border-white/10">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${colors.bg} flex items-center justify-center`}>
                          <mode.icon className={`w-5 h-5 ${colors.text}`} />
                        </div>
                        <div>
                          <div className="font-semibold text-sm text-foreground">{mode.title}</div>
                          <div className="text-xs text-muted-foreground">{mode.subtitle}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 rounded-lg glass text-xs">
                        <div className={`w-2 h-2 rounded-full ${colors.text.replace('text-', 'bg-')} animate-pulse`} />
                        <span className="text-muted-foreground">Active</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5 sm:p-6 bg-background/60 min-h-[200px]">
                      {mode.mockup}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoleplayShowcase;
